import { Fragment, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArchiveIcon,
  ArrowRightLeftIcon,
  BarChart3Icon,
  ChevronRightIcon,
  ClipboardListIcon,
  EyeOffIcon,
} from 'lucide-react';
import { PageShell } from '@/components/PageShell';
import { Card } from '@/components/ui/card';
import { MUTED } from '@/components/ui';
import { useAppInfo } from '@/lib/useAppInfo';
import { useAppReady } from '@/lib/useAppReady';
import { cn } from '@/lib/utils';

/**
 * 「百宝箱」——App 中间 tab 的落地页。
 *
 * 结构照搬搬小书原生百宝箱
 * `bxs_android/lib/features/toolbox/views/toolbox_view.dart`：
 * 一句说明 + 分组（section）+ 每组一张卡片，卡片里是 iOS 风格的设置行
 * （32 见方圆角图标 + 标题 / 副标题 + chevron，行间是缩进分隔线）。
 *
 * 两点刻意与它一致：
 * - **本页不选账号**：账号在进具体工具后再选（各页支持 `?accountId=` 预选 + 页内
 *   切换），对应搬小书入口的 `requiresAccount` —— 它也是进了工具才挑账号；
 * - 不放「运行环境」之类只有调试才关心的信息（要看版本走 App 的设置页）。
 */
const SECTIONS = [
  {
    id: 'tasks',
    title: '任务',
    items: [
      {
        id: 'tasks',
        title: '任务',
        subtitle: '查看备份、迁移等任务的进度与历史',
        icon: ClipboardListIcon,
        path: '/bxy/tasks',
      },
    ],
  },
  {
    id: 'data',
    title: '数据',
    items: [
      {
        id: 'backup',
        title: '数据备份',
        subtitle: '把作品、点赞、收藏、关注备份到本机',
        icon: ArchiveIcon,
        path: '/bxy/backup',
      },
      {
        id: 'migration',
        title: '账户迁移',
        subtitle: '把关注、收藏、笔记、点赞迁到另一个账号',
        icon: ArrowRightLeftIcon,
        path: '/bxy/migration',
      },
      {
        id: 'notes',
        title: '笔记处理',
        subtitle: '批量把抖音笔记设为仅自己可见，不删除',
        icon: EyeOffIcon,
        path: '/bxy/notes',
      },
      {
        id: 'data',
        title: '数据分析',
        subtitle: '看各账号播放/互动趋势与单篇笔记表现',
        icon: BarChart3Icon,
        path: '/bxy/data',
      },
    ],
  },
] as const;

export function Home() {
  // 只为拿原生主题（`app.info.theme` → 覆盖下方 .dark）；不渲染任何信息。
  const { loading } = useAppInfo();
  useAppReady(!loading);

  const navigate = useNavigate();
  const open = useCallback((path: string) => navigate(path), [navigate]);

  return (
    <PageShell title="百宝箱">
      <p className={MUTED}>集中管理任务和常用工具</p>

      {SECTIONS.map((section) => (
        <section key={section.id} className="flex flex-col gap-2">
          {/* iOS 分组标题：与卡片内图标的左边缘对齐（容器 16 + 行内 16） */}
          <h2 className={cn(MUTED, 'px-4 font-semibold')}>{section.title}</h2>
          <Card className="gap-0 rounded-card p-0">
            {section.items.map((item, index) => {
              const Icon = item.icon;
              return (
                <Fragment key={item.id}>
                  {/* 缩进分隔线：左对齐行内文字之外的图标，右侧留 12 */}
                  {index > 0 ? <div className="bg-border mr-3 ml-16 h-px shrink-0" /> : null}
                  <button
                    type="button"
                    data-haptic
                    onClick={() => open(item.path)}
                    className="active:bg-accent flex w-full items-center gap-4 px-4 py-3 text-left transition-colors"
                  >
                    <span className="bg-foreground/15 text-foreground flex size-8 shrink-0 items-center justify-center rounded-icon">
                      <Icon className="size-4" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <strong className="text-callout font-medium">{item.title}</strong>
                      <span className="text-muted-foreground text-footnote">{item.subtitle}</span>
                    </span>
                    <ChevronRightIcon className="text-muted-foreground size-4 shrink-0" />
                  </button>
                </Fragment>
              );
            })}
          </Card>
        </section>
      ))}
    </PageShell>
  );
}
